import React, {useMemo, useState} from 'react';
import classNames from 'classnames';
import {useInView} from 'react-intersection-observer';
import {Shimmer, Theme, ThemeName} from '@taopix/taopix-design-system';
import {CollectionLayout} from '../Product/LayoutSelector';
import {useSelector} from 'react-redux';
import {LayoutSelector} from '../../Selectors';

export type ThemedLayoutProps = {
  layout: CollectionLayout;
  onClick: (layout: CollectionLayout) => void;
};

const ThemedLayout = ({layout, onClick}: ThemedLayoutProps) => {
  const [loaded, setLoaded] = useState(false);
  const {ref, inView} = useInView({
    /* Optional options */
    threshold: 0.2,
  });

  const product = useSelector(LayoutSelector);

  const isSelected = useMemo(() => {
    return (
      product.collectioncode === layout.collection.collectionCode && product.code === layout.code
    );
  }, [product, layout]);

  const src = useMemo(() => {
    let thumb = `${window.config.baseURL}${layout.collection.thumbnailPath}/products/${layout.collection.collectionCode}/${layout.collection.thumbpath}/${layout.previewImage}`;
    if ('' === layout.previewImage) {
      thumb = 'https://placehold.co/100x100';
    }
    return thumb;
  }, [layout]);

  const thumbnailContainerClassess = classNames([
    'relative',
    'before:block',
    'before:pt-[100%]',
    'cursor-pointer',
    'mt-[var(--gap)]',
    'ml-[var(--gap)]',
    'overflow-hidden',
    'w-[calc((100%_-_(var(--gap)_*_var(--columns)))_/_var(--columns))]',
  ]);

  const thumbnailWrapperClasses = classNames(
    'absolute',
    'flex',
    'items-center',
    'justify-center',
    'p-xs',
    'top-0',
    'left-0',
    'cursor-pointer',
    'w-full',
    'h-full',
    'rounded-[min(theme(borderRadius.themeCornerSize),5px)]',
    isSelected
      ? ['outline', 'outline-[3px]', 'outline-offset-[-3px]', 'outline-themeAccentColour']
      : [
          'hover:outline',
          'hover:outline-[3px]',
          'hover:outline-offset-[-3px]',
          'hover:outline-themeAccentColour',
        ]
  );

  const thumbnailClassess = ['object-contain m-auto max-w-full max-h-full'];

  return (
    <li
      key={`li-${layout.collection.collectionUniqueRef}`}
      className={thumbnailContainerClassess}
      ref={ref}
    >
      {!loaded && <Shimmer className={'absolute top-0'} />}

      {inView && (
        <Theme
          name={ThemeName.Section}
          allowBackground
          allowCorners={false}
          className={thumbnailWrapperClasses}
          tabIndex={0}
          onKeyDown={event => {
            if ([' ', 'Enter'].includes(event.code) && !isSelected) {
              onClick(layout);
            }
          }}
        >
          {src !== '' ? (
            <img
              className={thumbnailClassess.join(' ')}
              src={src}
              draggable={true}
              alt={'sticker'}
              onLoad={e => {
                if ((e.target as HTMLImageElement).classList.contains('hidden')) {
                  (e.target as HTMLImageElement).classList.remove('hidden');
                }
                setLoaded(true);
              }}
              onError={e => {
                (e.target as HTMLImageElement).classList.add('hidden');
                // console.error(e);
              }}
              onClick={() => (isSelected ? () => {} : onClick(layout))}
            />
          ) : (
            <></>
          )}
        </Theme>
      )}
    </li>
  );
};
export default ThemedLayout;
